import useResizeObserver from "@react-hook/resize-observer";
import { Button, EllipsisIcon, flash, Popover, Tooltip } from "@xola/ui-kit";
import { useDeepCompareEffect } from "ahooks";
import clsx from "clsx";
import { PurchaseActionOption } from "common";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Permission } from "types";
import { Menu } from "../PurchaseMenu/Menu";
import { RosterActionOption } from "../PurchaseMenu/RosterActionOption";
import { SecondaryButton } from "./SecondaryButton";

interface ItemActionBarProps {
    actions: ItemActionOption[];
    className?: string;
    popoverParentId?: string;
    onButtonClick: (value: PurchaseActionOption | RosterActionOption) => void;
    children?: React.ReactNode;
}

const useResize = (target: any) => {
    /**
     * Observes resize of the buttons container and returns the element
     * width every time it gets changed. Is needed as a dependency for
     * the useEffect hook below to reposition the menu items between
     * the horizontal menubar and the stack hidden under the ellipsis button
     */
    const [size, setSize] = useState(0);

    useLayoutEffect(() => {
        setSize(target.current.getBoundingClientRect());
    }, [target]);

    useResizeObserver(target, (entry: any) => {
        setSize(entry.contentRect);
    });
    return size;
};

export const ItemActionBar = ({ actions, className, popoverParentId, onButtonClick, children }: ItemActionBarProps) => {
    const [isVisible, setIsVisible] = useState(false);

    const [actionItems, setActionItems] = useState<ItemActionOption[]>(actions);
    const [stackedActionItems, setStackedActionItems] = useState<ItemActionOption[] | [ItemActionOption]>([]);
    const target = useRef<HTMLDivElement>(null);

    const isResized = useResize(target);

    const isOverflowing = useCallback(() => {
        return target.current!.scrollWidth > target.current!.offsetWidth;
    }, []);

    // Callers rebuild `actions` on every render, so only a change in content may reset the overflow stack
    useDeepCompareEffect(() => {
        setActionItems(actions);
        setStackedActionItems([]);
    }, [actions]);

    const moveItemToStack = useCallback(() => {
        const lastActionItem = actionItems[actionItems.length - 1];
        // Action items always render as the leading DOM children (before injected `children` and the
        // overflow trigger), so the last one is at actionItems.length - 1. Do not derive this from
        // children.length: `children` (e.g. an EmbeddedAppTriggerLocation) adds sibling nodes that
        // would shift the index onto the wrong element.
        const lastActionItemWidth = target.current!.children[actionItems.length - 1].clientWidth;

        setStackedActionItems([{ ...lastActionItem, width: lastActionItemWidth }, ...stackedActionItems]);
        setActionItems(actionItems.filter((item) => item.title !== lastActionItem.title));
    }, [actionItems, stackedActionItems]);

    const moveItemToMenu = useCallback(() => {
        const firstStackedActionItem = stackedActionItems[0];

        setActionItems([...actionItems, firstStackedActionItem]);
        setStackedActionItems(stackedActionItems.filter((item) => item.title !== firstStackedActionItem.title));
    }, [actionItems, stackedActionItems]);

    useEffect(() => {
        /**
         * Detects if there's more menu items in the horzizontal menubar than it
         * can hold without horizontal scroll and moves the unfit ones to the stack
         * Pulls them back in case of enough space for the menu item is available
         */
        if (isOverflowing() && actionItems.length > 0) {
            moveItemToStack();
        } else if (stackedActionItems.length > 0) {
            let sumVisibleItemsWidth = 0;
            for (const item of target.current!.children) {
                sumVisibleItemsWidth += item.clientWidth;
            }

            if (target.current!.scrollWidth > sumVisibleItemsWidth + stackedActionItems[0].width! + 100) {
                moveItemToMenu();
            }
        }
    }, [isOverflowing, isResized, moveItemToStack, moveItemToMenu, actionItems, stackedActionItems]);

    const handleButtonClick = (action?: PurchaseActionOption | RosterActionOption) => {
        setIsVisible(false);

        if (typeof action === "undefined") {
            // Used typeof to avoid false null condition on first enum element
            getMessage("Not Implemented!");
        } else {
            onButtonClick(action);
        }
    };

    // Didn't find any other solutions
    // Action bar dynamic calculation in combination width dynamic Sidebar
    // leads to the problem that Purchase page has scrollBar, only for small screens
    // Below hacky way helps to resolve this problem
    const simpleBar: HTMLElement | null = document.querySelector(".simplebar-mask");
    const sideBar: HTMLElement | null = document.querySelector(".ui-sidebar"); // UI Kit should implement forwardRef for the sidebar
    const simpleBarWidth = simpleBar?.offsetWidth ?? 0;
    const sideBarWidth = sideBar?.offsetWidth ?? 0;
    const paddingWidth = window.innerWidth < 1280 ? 40 : 84; // xl px-10, default px-4
    const maxActionBarWidth = window.innerWidth - (sideBarWidth ?? 0) - simpleBarWidth - paddingWidth;

    let onlyActionClassName = "";
    if (actionItems.length === 1) {
        onlyActionClassName = `ui-only-${actionItems[0].title.toLowerCase().replace(/\s+/g, "-")}`;
    }

    return (
        <div
            ref={target}
            style={{ maxWidth: maxActionBarWidth }}
            className={clsx(
                "border-gray-light max-h-[330px] overflow-y-auto overflow-x-hidden whitespace-nowrap border-b",
                className,
                onlyActionClassName,
            )}
        >
            {actionItems.length > 0
                ? actionItems.map((item) => (
                      <div className={clsx(`ui-${item.title.toLowerCase().replace(/\s+/g, "-")}`)} key={item.title}>
                          <Tooltip
                              key={item.title}
                              content={item.tooltip ?? ""}
                              trigger={item.tooltip ? "mouseenter" : "manual"}
                          >
                              <Button
                                  variant="link"
                                  color={item.color ? item.color : "common"}
                                  size="small"
                                  className="whitespace-nowrap !text-base"
                                  disabled={!!item.disabled}
                                  icon={item.icon}
                                  onClick={() => handleButtonClick(item.action)}
                              >
                                  {item.title}
                              </Button>
                          </Tooltip>
                      </div>
                  ))
                : null}
            {children}
            {stackedActionItems.length > 0 ? (
                <Popover
                    visible={isVisible}
                    maxWidth={300}
                    offset={[0, 10]}
                    appendTo={popoverParentId ? document.querySelector(`#${popoverParentId}`) : document.body}
                    placement="left"
                    onClickOutside={() => setIsVisible(false)}
                >
                    <SecondaryButton
                        key="items-list"
                        size="small"
                        className="whitespace-nowrap focus:ring-0"
                        data-testid="item-action-bar-stacked-items-button"
                        onClick={() => setIsVisible(!isVisible)}
                    >
                        <EllipsisIcon />
                    </SecondaryButton>
                    <Popover.Content>
                        <Menu>
                            {stackedActionItems.map((item) => (
                                <Tooltip
                                    key={item.title}
                                    content={item.tooltip ?? ""}
                                    trigger={item.tooltip ? "mouseenter" : "manual"}
                                >
                                    <Menu.Item
                                        icon={item.icon}
                                        isDisabled={!!item.disabled}
                                        onClick={() => handleButtonClick(item.action)}
                                    >
                                        {item.title}
                                    </Menu.Item>
                                </Tooltip>
                            ))}
                        </Menu>
                    </Popover.Content>
                </Popover>
            ) : null}
        </div>
    );
};

const getMessage = (text: string) => {
    return flash.show({ text, color: "warning" });
};

export interface ItemActionOption {
    color?: string;
    disabled?: boolean;
    isLoading?: boolean;
    title: string;
    icon: JSX.Element;
    action: RosterActionOption | PurchaseActionOption;
    width?: number;
    permission?: Permission;
    enabled?: boolean;
    activeOnly?: boolean;
    suboptions?: ItemActionOption[];
    exclude?: boolean;
    tooltip?: string;
}
